import { useEffect, useSyncExternalStore } from 'react';
import { listenForResume } from './appResume';

/**
 * Verbindungszustand des Geräts, abgeleitet aus ECHTEN Anfragen (U15a). `navigator.onLine` ist auf Android im WLAN ohne
 * Internet („Lie-Fi“) `true`, deshalb führen die Ergebnisse der Anfragen:
 * - Antwort da: `online`
 * - Netzfehler oder Zeitlimit: `weak`, bei `navigator.onLine === false` `offline`
 * - Solange nicht `online` und die Seite sichtbar ist, prüft eine Probe `GET /api/health` alle 15 s.
 *
 * `onReconnect` meldet „jetzt lohnt sich ein Sendeversuch“: beim Übergang zu `online`, beim Sichtbarwerden der Seite und beim
 * Fortsetzen der App (`resume`). Mehrere Auslöser in kurzer Folge (z. B. Sichtbarkeit und `resume` zugleich) zählen einmal.
 */
export type ConnectionState = 'online' | 'weak' | 'offline';

/** Abstand der Probe, solange die Verbindung nicht `online` ist. */
export const PROBE_INTERVAL_MS = 15_000;
/** So lange wartet die Probe auf eine Antwort. */
export const PROBE_TIMEOUT_MS = 5_000;
/** Auslöser innerhalb dieser Zeit werden zu einem zusammengefasst. */
export const RECONNECT_COALESCE_MS = 500;

export interface ConnectivityOptions {
  /** Prüft den Server (`true` = Antwort da). Standard: `GET /api/health` mit Zeitlimit. */
  probe?: () => Promise<boolean>;
  /** Wert von `navigator.onLine`. */
  deviceOnline?: () => boolean;
  /** Ist die Seite sichtbar? */
  visible?: () => boolean;
}

export interface Connectivity {
  get: () => ConnectionState;
  subscribe: (listener: () => void) => () => void;
  /** Ruft `callback` bei jedem Anlass für einen neuen Sendeversuch. Gibt die Abmeldung zurück. */
  onReconnect: (callback: () => void) => () => void;
  /** Meldung des API-Clients: `true` = Antwort des Servers erhalten, `false` = Netzfehler oder Zeitlimit. */
  reportResult: (ok: boolean) => void;
  /** Hängt die Ereignisse ein und startet die Probe. Mehrfaches Aufrufen ist unschädlich. */
  start: () => void;
  stop: () => void;
}

async function defaultProbe(): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort();
  }, PROBE_TIMEOUT_MS);
  try {
    const response = await fetch('/api/health', {
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

export function createConnectivity(options: ConnectivityOptions = {}): Connectivity {
  const probe = options.probe ?? defaultProbe;
  const deviceOnline = options.deviceOnline ?? ((): boolean => navigator.onLine);
  const visible = options.visible ?? ((): boolean => document.visibilityState !== 'hidden');

  let state: ConnectionState = deviceOnline() ? 'online' : 'offline';
  let started = false;
  let probeTimer: ReturnType<typeof setTimeout> | null = null;
  let probing = false;
  let probeId = 0;
  let lastReconnectAt = Number.NEGATIVE_INFINITY;
  let removeResume: (() => void) | null = null;
  const listeners = new Set<() => void>();
  const reconnectListeners = new Set<() => void>();

  const emitReconnect = (): void => {
    const now = Date.now();
    if (now - lastReconnectAt < RECONNECT_COALESCE_MS) return;
    lastReconnectAt = now;
    for (const callback of [...reconnectListeners]) {
      try {
        callback();
      } catch {
        // Ein fehlerhafter Empfänger darf die anderen nicht stoppen
      }
    }
  };

  const clearProbe = (): void => {
    if (probeTimer !== null) clearTimeout(probeTimer);
    probeTimer = null;
  };

  const scheduleProbe = (delay: number): void => {
    clearProbe();
    if (!started || state === 'online') return;
    probeTimer = setTimeout(() => {
      probeTimer = null;
      void runProbe();
    }, delay);
  };

  const setState = (next: ConnectionState): void => {
    if (next === state) return;
    const before = state;
    state = next;
    for (const listener of [...listeners]) listener();
    if (next === 'online' && before !== 'online') {
      clearProbe();
      emitReconnect();
    } else if (next !== 'online') {
      scheduleProbe(PROBE_INTERVAL_MS);
    }
  };

  const failed = (): ConnectionState => (deviceOnline() ? 'weak' : 'offline');

  /**
   * Prüft den Server. `force`: Ein Anlass von außen (Netz gemeldet, Seite sichtbar) startet auch dann eine neue Probe, wenn
   * eine ältere noch hängt (Zeitlimit 5 s); deren spätes Ergebnis wird verworfen.
   */
  async function runProbe(force = false): Promise<void> {
    if (state === 'online' || (probing && !force)) return;
    // Verdeckte Seite: keine Probe (Akku, Daten); beim Sichtbarwerden geht es sofort weiter
    if (!visible()) {
      scheduleProbe(PROBE_INTERVAL_MS);
      return;
    }
    probeId += 1;
    const id = probeId;
    probing = true;
    try {
      const reachable = await probe();
      if (id !== probeId) return;
      setState(reachable ? 'online' : failed());
    } finally {
      if (id === probeId) probing = false;
    }
    scheduleProbe(PROBE_INTERVAL_MS);
  }

  /** Anlass „App wieder im Vordergrund“ oder „Netz gemeldet“: sofort prüfen, wenn nicht online, sonst nur senden. */
  const wake = (): void => {
    if (state === 'online') emitReconnect();
    else void runProbe(true);
  };

  const onOnline = (): void => {
    // Das Gerät meldet ein Netz: ob der Server erreichbar ist, entscheidet die Probe
    if (state === 'offline') setState('weak');
    wake();
  };
  const onOffline = (): void => {
    setState('offline');
  };
  const onVisibility = (): void => {
    if (visible()) wake();
  };

  return {
    get: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    onReconnect: (callback) => {
      reconnectListeners.add(callback);
      return () => {
        reconnectListeners.delete(callback);
      };
    },
    reportResult: (ok) => {
      setState(ok ? 'online' : failed());
    },
    start: () => {
      if (started) return;
      started = true;
      window.addEventListener('online', onOnline);
      window.addEventListener('offline', onOffline);
      document.addEventListener('visibilitychange', onVisibility);
      // App-`resume` (Capacitor): nur in der nativen Hülle; im Browser gibt es das Ereignis nicht
      listenForResume(wake)
        .then((remove) => {
          if (started) removeResume = remove;
          else remove();
        })
        .catch(() => undefined);
      if (state !== 'online') scheduleProbe(0);
    },
    stop: () => {
      if (!started) return;
      started = false;
      clearProbe();
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisibility);
      removeResume?.();
      removeResume = null;
    },
  };
}

/** Der Verbindungszustand dieser Seite (einmal je Seite). */
export const connectivity: Connectivity = createConnectivity();

/**
 * Der Zustand `online | weak | offline`, die Oberfläche zeichnet bei jedem Wechsel neu. `navigator.onLine === false` ist
 * verlässlich (nur `true` lügt): Dann gilt sofort `offline`, auch bevor ein Ereignis oder eine Anfrage es gemeldet hat.
 */
const snapshot = (): ConnectionState =>
  typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : connectivity.get();

export function useConnectivity(): ConnectionState {
  useEffect(() => {
    connectivity.start();
  }, []);
  return useSyncExternalStore(connectivity.subscribe, snapshot, snapshot);
}

/** Ruft `callback` bei Netz-Rückkehr, Sichtbarwerden und App-`resume`. */
export function onReconnect(callback: () => void): () => void {
  connectivity.start();
  return connectivity.onReconnect(callback);
}
